import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Layout.css';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileFeaturesOpen, setIsMobileFeaturesOpen] = useState(false);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef<number>(0);
  const touchStartY = useRef<number>(0);

  // Scroll to top when route changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setIsMobileFeaturesOpen(false);
  };

  // Handle touch events for swipe-to-close
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!isMobileMenuOpen) return;

    const touchEndX = e.changedTouches[0].clientX;
    const touchEndY = e.changedTouches[0].clientY;
    const deltaX = touchEndX - touchStartX.current;
    const deltaY = touchEndY - touchStartY.current;

    // Check if it's a horizontal swipe to the right (closing gesture)
    // and not a vertical scroll
    if (deltaX > 50 && Math.abs(deltaY) < 100) {
      closeMobileMenu();
    }
  };

  // Handle background click to close menu
  const handleBackgroundClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      closeMobileMenu();
    }
  };

  return (
    <div className="layout">
      {/* Header */}
      <header className="header">
        <div className="container">
          <nav className="nav">
            <Link to="/" className="nav-brand">
              <div className="brand-icon">
                <img src="/logo192.png" alt="TableChat" width="32" height="32" />
              </div>
              <span className="brand-text">TableChat&trade;</span>
            </Link>

            <div className="nav-links">
              <div className="nav-dropdown">
                <span className="nav-link nav-dropdown-trigger" role="button" aria-haspopup="true">
                  Features
                  <svg className="dropdown-arrow" width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 5L6 8L9 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
                <div className="nav-dropdown-menu">
                  <div className="nav-dropdown-menu-inner">
                    <Link to="/features/messaging" className="nav-dropdown-item">Guest Messaging</Link>
                    <Link to="/features/promotions" className="nav-dropdown-item">Guest Promotions</Link>
                    <Link to="/features/loyalty" className="nav-dropdown-item">VIP &amp; Loyalty</Link>
                    <Link to="/features/google-reviews" className="nav-dropdown-item">Google Reviews</Link>
                    <Link to="/features/smart-answers" className="nav-dropdown-item">Smart Answers</Link>
                    <Link to="/features/integrations" className="nav-dropdown-item">Integrations</Link>
                  </div>
                </div>
              </div>
              <Link to="/data" className="nav-link">The Data</Link>
              <Link to="/pricing" className="nav-link">Pricing</Link>
              <Link to="/faq" className="nav-link">FAQ</Link>
              <Link to="/contact" className="nav-link">Contact</Link>
              <a 
                href="https://console.tablechat.io/#/dashboard" 
                target="_blank" 
                rel="noopener noreferrer"
                className="nav-link"
              >
                Client Login
              </a>
              <Link to="/signup" className="btn btn-primary">Get Started</Link>
            </div>

            {/* Mobile menu button */}
            <button 
              className={`mobile-menu-btn ${isMobileMenuOpen ? 'active' : ''}`}
              onClick={toggleMobileMenu}
              aria-label="Toggle mobile menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
          </nav>

          {/* Mobile menu overlay */}
          <div 
            className={`mobile-menu ${isMobileMenuOpen ? 'active' : ''}`}
            onClick={handleBackgroundClick}
          >
            <div 
              className="mobile-menu-content"
              ref={mobileMenuRef}
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
            >
              {/* Mobile menu header with logo */}
              <div className="mobile-menu-header">
                <Link to="/" className="mobile-brand" onClick={closeMobileMenu}>
                  <div className="brand-icon">
                    <img src="/logo192.png" alt="TableChat" width="32" height="32" />
                  </div>
                  <span className="brand-text">TableChat&trade;</span>
                </Link>
                <button 
                  className="mobile-close-btn"
                  onClick={closeMobileMenu}
                  aria-label="Close mobile menu"
                >
                  ×
                </button>
              </div>

              {/* Mobile menu links */}
              <div className="mobile-menu-links">
                <button
                  className="mobile-nav-link mobile-features-toggle"
                  onClick={() => setIsMobileFeaturesOpen(!isMobileFeaturesOpen)}
                  aria-expanded={isMobileFeaturesOpen}
                >
                  Features
                  <svg className={`mobile-features-arrow ${isMobileFeaturesOpen ? 'open' : ''}`} width="16" height="16" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 5L6 8L9 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                <div className={`mobile-features-submenu ${isMobileFeaturesOpen ? 'open' : ''}`}>
                  <Link to="/features/messaging" className="mobile-nav-link mobile-sub-link" onClick={closeMobileMenu}>
                    Guest Messaging
                  </Link>
                  <Link to="/features/promotions" className="mobile-nav-link mobile-sub-link" onClick={closeMobileMenu}>
                    Guest Promotions
                  </Link>
                  <Link to="/features/loyalty" className="mobile-nav-link mobile-sub-link" onClick={closeMobileMenu}>
                    VIP &amp; Loyalty
                  </Link>
                  <Link to="/features/google-reviews" className="mobile-nav-link mobile-sub-link" onClick={closeMobileMenu}>
                    Google Reviews
                  </Link>
                  <Link to="/features/smart-answers" className="mobile-nav-link mobile-sub-link" onClick={closeMobileMenu}>
                    Smart Answers
                  </Link>
                  <Link to="/features/integrations" className="mobile-nav-link mobile-sub-link" onClick={closeMobileMenu}>
                    Integrations
                  </Link>
                </div>
                <Link to="/data" className="mobile-nav-link" onClick={closeMobileMenu}>
                  The Data
                </Link>
                <Link to="/pricing" className="mobile-nav-link" onClick={closeMobileMenu}>
                  Pricing
                </Link>
                <Link to="/faq" className="mobile-nav-link" onClick={closeMobileMenu}>
                  FAQ
                </Link>
                <Link to="/contact" className="mobile-nav-link" onClick={closeMobileMenu}>
                  Contact
                </Link>
                <Link to="/signup" className="mobile-nav-link" onClick={closeMobileMenu}>
                  Get Started
                </Link>
                <Link to="/privacy" className="mobile-nav-link" onClick={closeMobileMenu}>
                  Privacy Policy
                </Link>
                <Link to="/terms" className="mobile-nav-link" onClick={closeMobileMenu}>
                  Terms of Service
                </Link>
                <Link to="/messaging-policy" className="mobile-nav-link" onClick={closeMobileMenu}>
                  Messaging Policy
                </Link>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="main">
        {children}
      </main>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <div className="footer-brand">
                <div className="brand-icon">
                  <img src="/logo192.png" alt="TableChat" width="32" height="32" />
                </div>
                <div>
                  <div className="brand-text">TableChat&trade;</div>
                </div>
              </div>
              <p className="footer-description">
                Elevate your guest engagement with TableChat.
              </p>
              <p className="footer-made-in">
                Dreamed, Designed, Developed in <img src="/american-flag.png" alt="United States" className="footer-flag" />
              </p>
            </div>

            <div className="footer-section">
              <h4>Features</h4>
              <ul>
                <li><Link to="/features/messaging">Guest Messaging</Link></li>
                <li><Link to="/features/promotions">Guest Promotions</Link></li>
                <li><Link to="/features/loyalty">VIP &amp; Loyalty</Link></li>
                <li><Link to="/features/google-reviews">Google Reviews</Link></li>
                <li><Link to="/features/smart-answers">Smart Answers</Link></li>
                <li><Link to="/features/integrations">Integrations</Link></li>
              </ul>
            </div>

            <div className="footer-section">
              <h4>Product</h4>
              <ul>
                <li><Link to="/data">The Data</Link></li>
                <li><Link to="/pricing">Pricing</Link></li>
                <li><Link to="/faq">FAQ</Link></li>
                <li><Link to="/contact">Contact Us</Link></li>
                <li><Link to="/signup">Get Started</Link></li>
                <li className="desktop-only">
                  <a 
                    href="https://console.tablechat.io/#/dashboard" 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    Client Login
                  </a>
                </li>
              </ul>
            </div>

            <div className="footer-section">
              <h4>Legal</h4>
              <ul>
                <li><Link to="/privacy">Privacy Policy</Link></li>
                <li><Link to="/terms">Terms of Service</Link></li>
                <li><Link to="/messaging-policy">Messaging Policy</Link></li>
              </ul>
              <h4 className="footer-social-heading">Connect</h4>
              <div className="footer-social-links">
                <a href="https://www.instagram.com/tablechat.io" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="footer-social-link">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                  </svg>
                </a>
                <a href="https://www.linkedin.com/company/110435841" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="footer-social-link">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
                <a href="https://x.com/ddhhayes" target="_blank" rel="noopener noreferrer" aria-label="X (Twitter)" className="footer-social-link">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>

          <div className="footer-bottom">
            <p>&copy; {new Date().getFullYear()} TableChat&trade;. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
